import React, { useEffect, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, IconButton, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Paper, Typography
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
    adminFetchPackagesThunk,
    adminCreatePackageThunk,
    adminUpdatePackageThunk,
    adminDeletePackageThunk,
    selectAllAdminPackages,
    selectAdminPackagesApi,
    selectAdminPackageCreateApi,
    selectAdminPackageUpdateApi,
    selectAdminPackageDeleteApi,
    resetAdminPackageMutationApis,
} from 'store/reducers/temple/whatsappSlice';
import { WhatsAppPackageType, AdminPackageRequest } from 'api/whatsapp/types';
import { apiStatus } from 'shared/constants/AppEnum';
import { useSnackbar } from 'notistack';
import PackageFormDialog from './PackageFormDialog';
import HeadingCard from 'components/Card/HeadingCard';

const AdminPackageTable: React.FC = () => {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const packages = useAppSelector(selectAllAdminPackages);
    const listApi = useAppSelector(selectAdminPackagesApi);
    const createApi = useAppSelector(selectAdminPackageCreateApi);
    const updateApi = useAppSelector(selectAdminPackageUpdateApi);
    const deleteApi = useAppSelector(selectAdminPackageDeleteApi);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingPkg, setEditingPkg] = useState<(WhatsAppPackageType & { id: number }) | null>(null);

    useEffect(() => {
        dispatch(adminFetchPackagesThunk());
    }, [dispatch]);

    // Handle create/update success
    useEffect(() => {
        if (createApi.status === apiStatus.SUCCEEDED) {
            enqueueSnackbar('Package created successfully', { variant: 'success' });
            setDialogOpen(false);
            dispatch(resetAdminPackageMutationApis());
        }
        if (updateApi.status === apiStatus.SUCCEEDED) {
            enqueueSnackbar('Package updated successfully', { variant: 'success' });
            setDialogOpen(false);
            setEditingPkg(null);
            dispatch(resetAdminPackageMutationApis());
        }
        if (deleteApi.status === apiStatus.SUCCEEDED) {
            enqueueSnackbar('Package deleted successfully', { variant: 'success' });
            dispatch(resetAdminPackageMutationApis());
        }
    }, [createApi.status, updateApi.status, deleteApi.status, enqueueSnackbar, dispatch]);

    const handleCreate = () => {
        setEditingPkg(null);
        setDialogOpen(true);
    };

    const handleEdit = (pkg: WhatsAppPackageType) => {
        setEditingPkg(pkg as WhatsAppPackageType & { id: number });
        setDialogOpen(true);
    };

    const handleDelete = (id: number) => {
        if (window.confirm('Are you sure you want to delete this package?')) {
            dispatch(adminDeletePackageThunk(id));
        }
    };

    const handleSubmit = (data: AdminPackageRequest) => {
        if (editingPkg) {
            dispatch(adminUpdatePackageThunk({ id: editingPkg.id, data }));
        } else {
            dispatch(adminCreatePackageThunk(data));
        }
    };

    const handleClose = () => {
        setDialogOpen(false);
        setEditingPkg(null);
        dispatch(resetAdminPackageMutationApis());
    };

    return (
        <>
            <HeadingCard title="Credit Packages (Admin)" cardContent={false} hasheaderBackground={false}>
                <Box display="flex" justifyContent="flex-end" p={2}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleCreate}
                    >
                        Add Package
                    </Button>
                </Box>
                {listApi.status === apiStatus.LOADING ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : packages.length === 0 ? (
                    <Box textAlign="center" my={4}>
                        <Typography color="textSecondary">No packages found</Typography>
                    </Box>
                ) : (
                    <TableContainer component={Paper} elevation={0}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell align="right">Price</TableCell>
                                    <TableCell align="right">Messages</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {packages.map((pkg) => (
                                    <TableRow key={pkg.id}>
                                        <TableCell>{pkg.name}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={pkg.package_type_display || pkg.package_type}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right">₹{pkg.price}</TableCell>
                                        <TableCell align="right">{pkg.message_count || (pkg as any).messageCount}</TableCell>
                                        <TableCell>{pkg.description}</TableCell>
                                        <TableCell align="center">
                                            <IconButton size="small" onClick={() => handleEdit(pkg)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleDelete(pkg.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </HeadingCard>

            <PackageFormDialog
                open={dialogOpen}
                onClose={handleClose}
                onSubmit={handleSubmit}
                api={editingPkg ? updateApi : createApi}
                initialData={editingPkg ? {
                    name: editingPkg.name,
                    package_type: editingPkg.package_type,
                    price: editingPkg.price,
                    message_count: editingPkg.message_count || (editingPkg as any).messageCount,
                    description: editingPkg.description,
                    id: editingPkg.id,
                } : undefined}
                mode={editingPkg ? 'edit' : 'create'}
            />
        </>
    );
};

export default AdminPackageTable;
